import { useEffect, useRef, useState } from 'react';
import { useStickersControllerRecreateStickersPack } from '~/lib/api';

const RATE_LIMIT_STATUS = 429;
const DEFAULT_COUNTDOWN_SECONDS = 60;

interface UseRefreshStickersOptions {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

interface UseRefreshStickersReturn {
  recreateStickersPack: ReturnType<typeof useStickersControllerRecreateStickersPack>['mutateAsync'];
  data: ReturnType<typeof useStickersControllerRecreateStickersPack>['data'];
  error: ReturnType<typeof useStickersControllerRecreateStickersPack>['error'];
  isPending: boolean;
  activeRefreshCount: number;
  isButtonDisabled: boolean;
}

export function useRefreshStickers(options: UseRefreshStickersOptions = {}): UseRefreshStickersReturn {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeRefreshCount, setActiveRefreshCount] = useState(0);

  const {
    mutateAsync: recreateStickersPack,
    data,
    error,
    isPending,
  } = useStickersControllerRecreateStickersPack({
    mutation: {
      onMutate: () => {
        // Clear any existing timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setActiveRefreshCount(DEFAULT_COUNTDOWN_SECONDS);
      },
      onError: (mutationError: unknown) => {
        // Check if it's a rate limit error (429)
        const httpError = mutationError as {
          response?: { status?: number; data?: { secondsRemaining?: number } };
        };
        if (httpError.response?.status === RATE_LIMIT_STATUS && httpError.response.data?.secondsRemaining) {
          const seconds = httpError.response.data.secondsRemaining;
          setActiveRefreshCount(seconds);
        }
        options.onError?.(mutationError);
      },
      onSuccess: () => {
        // Keep 60s countdown even on success to prevent immediate re-calls
        // Don't reset to 0, let the timer count down naturally
        options.onSuccess?.();
      },
    },
  });

  // Timer effect for countdown
  useEffect(() => {
    if (activeRefreshCount > 0) {
      timerRef.current = setInterval(() => {
        setActiveRefreshCount((previous) => {
          if (previous <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            return 0;
          }
          return previous - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [activeRefreshCount]);

  return {
    recreateStickersPack,
    data,
    error,
    isPending,
    activeRefreshCount,
    isButtonDisabled: activeRefreshCount > 0 || isPending,
  };
}
